import { useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, diasEntre, formatFxRate, formatQuantity, fxRate, hoy, sumarDias } from '@abastece/shared';
import { estadoResultados, utilidadPorCliente, utilidadPorProducto, type FormaPagoVenta, type Venta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import {
  PERIODOS_ANALISIS,
  alzasDeCosto,
  bajoElMargen,
  busquedaDeAnalisis,
  calorPorHora,
  cambioBp,
  clientesQueSeFueron,
  concentracion,
  cuentasConFiado,
  delCatalogo,
  diasDe,
  dineroDeAnalisis,
  equilibrio,
  esSoloHoy,
  fiadoPorAntiguedad,
  filasDichas,
  horaPico,
  mejorPunto,
  mercaderiaParada,
  partesDePago,
  resumenDeVentas,
  serieDeVentas,
  tramosDe,
  ultimaCompraPorProducto,
  ultimaVentaPorProducto,
  type BusquedaAnalisis,
  type DineroDeAnalisis,
  type Paso,
  type PeriodoAnalisis,
  type Punto,
  type Tramo,
  type TramoFiado,
  type Tramos,
  type VistaAnalisis,
} from '../lib/analisis.js';
import { cobradoPorForma } from '../lib/cuadrar.js';
import { DIAS_DE_CLIENTE, fiadoDelPeriodo, lesTocaComprar, riesgoDelFiado } from '../lib/clientela.js';
import { horasDeCaja, perdidasDelPeriodo } from '../lib/egresos.js';
import { agotados, muchoEnElEstante } from '../lib/estante.js';
import { etiquetaRango, otroMes } from '../lib/historial.js';
import {
  diasDelRango,
  elegirMonedaDeLosLibros,
  momentosDeVentas,
  monedaDeLosLibros,
  ventasDelPeriodo,
  type MonedaDeLosLibros,
} from '../lib/libros.js';
import { usePuede } from '../lib/sesion.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaCuenta, etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { ComoVaElFiado, LesTocaComprar } from '../ui/AnalisisClientes.js';
import { VistaCompras } from '../ui/AnalisisCompras.js';
import { VistaDinero } from '../ui/AnalisisDinero.js';
import { VistaHoy } from '../ui/AnalisisHoy.js';
import { HorasDeCaja, LaPlanilla, LoQueSePierde, MeAlcanza, PresupuestoDelMes } from '../ui/AnalisisGastos.js';
import { GastosDelPeriodo, ResultadoDelPeriodo, VentasDelPeriodo } from '../ui/AnalisisDetalles.js';
import { Agotados, MuchoEnElEstante, PorCategoria, SimularUnPrecio } from '../ui/AnalisisProductos.js';
import { ComoVaElMes, PorCanal, PorDiaDelMes, PorLista } from '../ui/AnalisisVentas.js';
import { ElegirMoneda } from '../ui/ElegirMoneda.js';
import { Calendario } from '../ui/FiltrosHistorial.js';
import { Barras, Cambio, Cifra, Columnas, MapaDeCalor } from '../ui/Graficas.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { ACliente, AProducto, Cargando, Corta, CUANTOS, Fila, Nada, Seccion, parte, pct, useFechas } from '../ui/PiezasDeAnalisis.js';

/**
 * «Análisis»: how the shop is doing, as the questions an owner asks (`lib/analisis.ts`). Four
 * tabs, sales, products, clients and costs, over a period compared with the one before it.
 *
 * Each tab reads only what it shows, and only while it is open: a year of sales is a lot to read
 * on a cheap phone, and the costs never need it.
 */

const VISTAS: readonly { clave: VistaAnalisis; nombre: Clave }[] = [
  { clave: 'hoy', nombre: 'analisis.hoy' },
  { clave: 'ventas', nombre: 'analisis.ventas' },
  { clave: 'productos', nombre: 'analisis.productos' },
  { clave: 'clientes', nombre: 'analisis.clientes' },
  { clave: 'compras', nombre: 'analisis.compras' },
  { clave: 'gastos', nombre: 'analisis.gastos' },
  { clave: 'dinero', nombre: 'analisis.dinero' },
];

const PERIODO: Record<Exclude<PeriodoAnalisis, 'rango'>, Clave> = {
  hoy: 'periodo.hoy',
  ayer: 'periodo.ayer',
  mes: 'periodo.esteMes',
  mesPasado: 'periodo.mesPasado',
  tresMeses: 'analisis.tresMeses',
  anio: 'periodo.esteAnio',
};

const POR_PASO: Record<Paso, Clave> = { dia: 'analisis.porDia', semana: 'analisis.porSemana', mes: 'analisis.porMes' };
const MEJOR: Record<Paso, Clave> = { dia: 'analisis.mejorDia', semana: 'analisis.mejorSemana', mes: 'analisis.mejorMes' };

const FORMA: Record<FormaPagoVenta, Clave> = {
  efectivo_cordobas: 'pago.efectivo_cordobas',
  efectivo_dolares: 'pago.efectivo_dolares',
  transferencia: 'pago.transferencia',
  billetera: 'pago.billetera',
  tarjeta: 'pago.tarjeta',
  credito: 'pago.fiado',
};

const TRAMO: Record<TramoFiado, { nombre: Clave; tono: string }> = {
  '30': { nombre: 'analisis.tramo30', tono: 'bg-emerald-500' },
  '60': { nombre: 'analisis.tramo60', tono: 'bg-amber-400' },
  '90': { nombre: 'analisis.tramo90', tono: 'bg-orange-500' },
  mas: { nombre: 'analisis.tramoMas', tono: 'bg-rose-500' },
};

/** How far back a sale counts for when a product last sold. */
const DIAS_DE_VENTAS = 90;
/** Days without a sale or a purchase that make stock «parada»: the purchases read and the cut are one. */
const DIAS_PARADO = 30;

const masAntigua = (a: string, b: string): string => (a < b ? a : b);

export function Analisis() {
  const t = useT();
  const navigate = useNavigate();
  const puedeVer = usePuede('ver_reportes');
  // Read through the same filter the route's `validateSearch` is: the router hands on every
  // parameter of the address whatever that kept (the root route strips none), and a link typed by
  // hand or left from an older version, «?periodo=siempre», must open on this month's sales, not on
  // a period with no days.
  const busqueda = busquedaDeAnalisis(useSearch({ from: '/analisis' }));
  const vista = busqueda.vista ?? 'hoy';
  const periodo = busqueda.periodo ?? 'mes';
  const hoyEs = hoy();
  const { desde, hasta } = busqueda;
  const tramos = useMemo(
    () => tramosDe(periodo, hoyEs, desde && hasta ? { desde, hasta } : undefined),
    [periodo, hoyEs, desde, hasta],
  );
  const locale = localeActual();
  /** Córdobas or dollars, every figure on the page: the choice «Contabilidad» is read in, kept on the phone. */
  const [moneda, setMoneda] = useState<MonedaDeLosLibros>(monedaDeLosLibros);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** The rate the books are kept at (`negocio.tasaOficialAyuda`), as «Contabilidad» reads them in dollars. */
  const tasa = fxRate(config.fxTasaOficial);
  const dinero = useMemo(() => dineroDeAnalisis(moneda, tasa), [moneda, tasa]);
  const elegirMoneda = (m: MonedaDeLosLibros) => {
    setMoneda(m);
    elegirMonedaDeLosLibros(m);
  };
  /** Two days of the owner's own, picked on the calendar the histories use (`Calendario`). */
  const [calendario, setCalendario] = useState(false);
  const f = useFechas();
  /** A tab about now, not a period: the periods are not asked. */
  const delMomento = vista === 'hoy' || vista === 'dinero';

  if (!puedeVer) {
    return (
      <Pagina titulo={t('analisis.titulo')}>
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }

  // The same screen with another tab or period: nothing slides, as «Gastos» drops its `nuevo`. The
  // router's types already say so where a browser has them (`tiposDeTransicion`); where it has not,
  // every change of address would slide the whole page. Days picked stay with another tab, and go
  // with another period.
  const ir = (cambio: BusquedaAnalisis) =>
    void navigate({
      to: '/analisis',
      search: busquedaDeAnalisis({ vista, periodo, desde, hasta, ...cambio }),
      replace: true,
      viewTransition: false,
    });
  /** A period's button; `mas`, what one of them adds of its own. */
  const boton = (activo: boolean, mas = '') =>
    `rounded-lg px-3 py-2 text-sm transition max-[359px]:px-2 max-[359px]:text-[13px] ${activo ? 'bg-marca-600 font-medium text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'} ${mas}`;

  return (
    <Pagina
      titulo={t('analisis.titulo')}
      subtitulo={delMomento ? t('analisis.hoyEs', { fecha: f.conDia(hoyEs) }) : etiquetaRango(tramos.actual.desde, tramos.actual.hasta, hoyEs, locale)}
      ancho="amplio"
    >
      {/* The periods, and beside them, or under them on a phone, the currency they are read in. */}
      <div className="mb-3 flex flex-wrap items-start gap-2">
        {/* Six, two by two on a phone, and the calendar's under them: the row wraps evenly instead of scrolling. */}
        <div className={`grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap ${delMomento ? 'hidden' : ''}`} role="group" aria-label={t('analisis.periodo')} data-analisis-periodos>
          {PERIODOS_ANALISIS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => ir({ periodo: p })}
              aria-pressed={p === periodo}
              data-sin-icono="pestana"
              data-periodo={p}
              // «Últimos 3 meses» on one line in half a 320 px phone.
              className={boton(p === periodo)}
            >
              {t(PERIODO[p])}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setCalendario(true)}
            aria-pressed={periodo === 'rango'}
            data-periodo="rango"
            className={boton(periodo === 'rango', 'col-span-2 inline-flex items-center justify-center gap-1.5')}
          >
            <Ico icono="calendario" className="size-4" />
            {periodo === 'rango' ? etiquetaRango(tramos.actual.desde, tramos.actual.hasta, hoyEs, locale) : t('historial.periodo.elegir')}
          </button>
        </div>
        <ElegirMoneda moneda={moneda} onElegir={elegirMoneda} />
      </div>
      {moneda === 'USD' && (
        <p className="-mt-1 mb-3 text-xs text-slate-500" data-analisis-tasa>
          {t('analisis.enDolares', { tasa: formatFxRate(tasa) })}
        </p>
      )}
      {calendario && (
        <Calendario
          desde={periodo === 'rango' ? tramos.actual.desde : ''}
          hasta={periodo === 'rango' ? tramos.actual.hasta : ''}
          hoyEs={hoyEs}
          onListo={(d, h) => {
            setCalendario(false);
            ir({ periodo: 'rango', desde: d, hasta: h });
          }}
          onCerrar={() => setCalendario(false)}
        />
      )}

      {/*
        On a phone the first four in a row and the rest in a row under them, each tab as wide as its
        word and the room left shared out, as the section tabs are: four equal quarters cut
        «Productos» short on a 360 px phone. From 640 px the rows fold away (`contents`) and every
        tab is in one row.
      */}
      <nav className="mb-5 flex flex-col gap-1 rounded-2xl bg-slate-900 p-1 ring-1 ring-white/5 ring-inset sm:flex-row" aria-label={t('analisis.vistas')} data-analisis-vistas>
        {[VISTAS.slice(0, 4), VISTAS.slice(4)].map((fila, i) => (
          <div key={i} className="flex gap-1 sm:contents">
            {fila.map((v) => {
              const activa = v.clave === vista;
              return (
                <button
                  key={v.clave}
                  type="button"
                  onClick={() => ir({ vista: v.clave })}
                  aria-pressed={activa}
                  data-sin-icono="pestana"
                  data-vista={v.clave}
                  // Smaller type below 380 px.
                  className={`flex min-h-11 min-w-0 flex-auto items-center justify-center rounded-xl px-1.5 text-sm transition max-[379px]:text-[13px] max-[339px]:px-1 max-[339px]:text-xs ${
                    activa
                      ? 'bg-slate-700/80 font-semibold text-white shadow-sm shadow-black/30 ring-1 ring-white/10 ring-inset'
                      : 'font-medium text-slate-400 hover:bg-white/5 hover:text-slate-200'
                  }`}
                >
                  <span className="truncate">{t(v.nombre)}</span>
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {vista === 'hoy' && <VistaHoy hoyEs={hoyEs} dinero={dinero} />}
      {vista === 'ventas' && <VistaVentas tramos={tramos} hoyEs={hoyEs} dinero={dinero} esteMes={periodo === 'mes'} />}
      {vista === 'productos' && <VistaProductos tramos={tramos} hoyEs={hoyEs} dinero={dinero} />}
      {vista === 'clientes' && <VistaClientes tramos={tramos} hoyEs={hoyEs} dinero={dinero} />}
      {vista === 'compras' && <VistaCompras tramos={tramos} hoyEs={hoyEs} dinero={dinero} />}
      {vista === 'gastos' && <VistaGastos tramos={tramos} hoyEs={hoyEs} dinero={dinero} deUnMes={periodo === 'mes' || periodo === 'mesPasado'} />}
      {vista === 'dinero' && <VistaDinero hoyEs={hoyEs} dinero={dinero} />}
    </Pagina>
  );
}

// ---------------------------------------------------------------------------
// Ventas
// ---------------------------------------------------------------------------

function VistaVentas({ tramos, hoyEs, dinero, esteMes }: { tramos: Tramos; hoyEs: string; dinero: DineroDeAnalisis; esteMes: boolean }) {
  const t = useT();
  const f = useFechas();
  const locale = localeActual();
  const { actual, anterior, paso } = tramos;
  /** The period's sales in a sheet: what the four figures add up. */
  const [detalle, setDetalle] = useState(false);
  const datos = useLiveQuery(async () => {
    const ventas = await ventasDelPeriodo({ desde: anterior.desde, hasta: actual.hasta });
    return { ventas, momentos: await momentosDeVentas(ventas, actual) };
  }, [actual.desde, actual.hasta, anterior.desde]);

  const calculo = useMemo(() => {
    if (!datos) return null;
    const delPeriodo = datos.ventas.filter((v) => v.fecha >= actual.desde && v.fecha <= actual.hasta);
    const serie = serieDeVentas(datos.ventas, actual, paso);
    return {
      ahora: resumenDeVentas(datos.ventas, actual),
      antes: resumenDeVentas(datos.ventas, anterior),
      serie,
      mejor: mejorPunto(serie),
      pagos: partesDePago(cobradoPorForma(delPeriodo).porForma),
      calor: calorPorHora(datos.momentos),
    };
  }, [datos, actual, anterior, paso]);

  if (!calculo) return <Cargando />;
  const { ahora, antes, serie, mejor, pagos, calor } = calculo;

  const nombrePunto = (p: Punto): string =>
    paso === 'dia' ? f.conDia(p.desde) : paso === 'mes' ? f.mesLargo(p.desde) : etiquetaRango(p.desde, p.hasta, hoyEs, locale);
  const marca = (p: Punto): string => (paso === 'mes' ? f.mesCorto(p.desde) : f.corta(p.desde));
  const pico = calor ? horaPico(calor) : null;

  return (
    <div className="flex flex-col gap-4" data-analisis="ventas">
      <div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Cifra
            nombre={t('analisis.vendido')}
            valor={dinero(ahora.ingreso)}
            debajo={<Cambio bp={cambioBp(ahora.ingreso, antes.ingreso)} />}
            datos="vendido"
            onAbrir={() => setDetalle(true)}
          />
          <Cifra
            nombre={t('analisis.utilidad')}
            valor={dinero(ahora.utilidad)}
            negativo={ahora.utilidad < 0}
            debajo={
              <>
                <Cambio bp={cambioBp(ahora.utilidad, antes.utilidad)} />
                <span className="text-xs text-slate-500">{t('analisis.margen', { pct: pct(ahora.margenBp) })}</span>
              </>
            }
            datos="utilidad"
            onAbrir={() => setDetalle(true)}
          />
          <Cifra
            nombre={t('analisis.numVentas')}
            valor={ahora.ventas.toLocaleString(locale)}
            debajo={<Cambio bp={cambioBp(ahora.ventas, antes.ventas)} />}
            datos="ventas"
            onAbrir={() => setDetalle(true)}
          />
          <Cifra
            nombre={t('analisis.ticket')}
            valor={ahora.ticket === null ? '—' : dinero(ahora.ticket)}
            debajo={<Cambio bp={ahora.ticket === null || antes.ticket === null ? null : cambioBp(ahora.ticket, antes.ticket)} />}
            datos="ticket"
            onAbrir={() => setDetalle(true)}
          />
        </div>
        <p className="mt-2 text-xs text-slate-500" data-comparado>
          {t(antes.ventas === 0 ? 'analisis.sinAntes' : esSoloHoy(actual, hoyEs) ? 'analisis.comparadoConDiaEntero' : 'analisis.comparadoCon', {
            rango: etiquetaRango(anterior.desde, anterior.hasta, hoyEs, locale),
          })}
        </p>
      </div>

      {esteMes && <ComoVaElMes hoyEs={hoyEs} dinero={dinero} />}

      {/* One day has one column: its hours are under «Cuándo se vende». */}
      {actual.desde !== actual.hasta && (
      <Seccion titulo={t(POR_PASO[paso])} datos="serie">
        {ahora.ventas === 0 ? (
          <Nada texto={t('analisis.sinVentas')} />
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
              <span className="flex items-center gap-1.5">
                <span className="size-2.5 rounded-sm bg-marca-500/35" aria-hidden />
                {t('analisis.leyendaVendido')}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="size-2.5 rounded-sm bg-marca-400" aria-hidden />
                {t('analisis.leyendaUtilidad')}
              </span>
            </div>
            <Columnas
              columnas={serie.map((p) => ({
                clave: p.desde,
                etiqueta: marca(p),
                valor: p.ingreso,
                parte: p.utilidad,
                texto: t('analisis.columna', { fecha: nombrePunto(p), vendido: dinero(p.ingreso), utilidad: dinero(p.utilidad), n: p.ventas }),
              }))}
              resumen={`${t(POR_PASO[paso])}: ${mejor ? t(MEJOR[paso], { fecha: nombrePunto(mejor), monto: dinero(mejor.ingreso) }) : ''}`}
            />
            <p className="mt-3 text-sm text-slate-400" data-promedio>
              {t('analisis.promedioDia', { monto: dinero(ahora.ingreso / diasDe(actual)) })}
              {mejor && ` · ${t(MEJOR[paso], { fecha: nombrePunto(mejor), monto: dinero(mejor.ingreso) })}`}
            </p>
          </>
        )}
      </Seccion>
      )}

      <PorCanal ventas={datos!.ventas} tramo={actual} dinero={dinero} />
      <PorLista ventas={datos!.ventas} tramo={actual} dinero={dinero} />

      <Seccion titulo={t('analisis.comoPagan')} datos="pagos">
        {pagos.length === 0 ? (
          <Nada texto={t('analisis.sinVentas')} />
        ) : (
          <Barras
            filas={pagos.map((p) => ({
              clave: p.forma,
              etiqueta: t(FORMA[p.forma as FormaPagoVenta] ?? 'pago.credito'),
              valor: p.monto,
              texto: `${dinero(p.monto)} · ${pct(p.parteBp)}`,
            }))}
          />
        )}
      </Seccion>

      <Seccion titulo={t('analisis.cuandoSeVende')} ayuda={t('analisis.cuandoAyuda')} datos="horas">
        {!calor || !pico ? (
          <Nada texto={t('analisis.sinVentas')} />
        ) : (
          <>
            <MapaDeCalor
              celdas={calor.celdas}
              max={calor.max}
              desde={calor.desde}
              hasta={calor.hasta}
              dias={f.diasCortos}
              resumen={t('analisis.horaPico', { dia: f.diasLargos[pico.dia]!, desde: `${pico.hora}:00`, hasta: `${pico.hora + 1}:00` })}
            />
            <p className="mt-3 text-sm text-slate-300" data-hora-pico>
              {t('analisis.horaPico', { dia: f.diasLargos[pico.dia]!, desde: `${pico.hora}:00`, hasta: `${pico.hora + 1}:00` })}
            </p>
          </>
        )}
      </Seccion>
      <PorDiaDelMes hoyEs={hoyEs} dinero={dinero} />
      {detalle && datos && <VentasDelPeriodo ventas={datos.ventas} tramo={actual} dinero={dinero} onCerrar={() => setDetalle(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Productos
// ---------------------------------------------------------------------------

function VistaProductos({ tramos, hoyEs, dinero }: { tramos: Tramos; hoyEs: string; dinero: DineroDeAnalisis }) {
  const t = useT();
  const f = useFechas();
  const nombre = useNombreProducto();
  const { actual } = tramos;
  const datos = useLiveQuery(async () => {
    const ventana = sumarDias(hoyEs, -(DIAS_DE_VENTAS - 1));
    const [ventas, productos, existencias, compras, primera, config, reempaques, salidas, conteos] = await Promise.all([
      ventasDelPeriodo({ desde: masAntigua(actual.desde, ventana), hasta: hoyEs }),
      db.productos.toArray(),
      db.existencias.toArray(),
      // Only whether it came in lately: what just arrived has not had the chance to sell.
      db.compras.where('fecha').aboveOrEqual(sumarDias(hoyEs, -DIAS_PARADO)).toArray(),
      db.ventas.orderBy('fecha').first(),
      readSettings(),
      // What else takes stock out: when something ran out, and how fast what is bagged goes.
      db.reempaques.where('fecha').aboveOrEqual(ventana).toArray(),
      db.salidas.where('fecha').aboveOrEqual(ventana).toArray(),
      db.conteos.where('fecha').aboveOrEqual(ventana).toArray(),
    ]);
    return {
      ventas,
      productos,
      existencias,
      compras,
      reempaques,
      salidas,
      conteos,
      ventana,
      primeraVenta: primera?.fecha ?? null,
      minimoBp: config.precioMargenMinimoBp,
    };
  }, [actual.desde, actual.hasta, hoyEs]);

  const calculo = useMemo(() => {
    if (!datos) return null;
    const porId = new Map(datos.productos.map((p) => [p.id, p]));
    const nombreDe = (id: string) => {
      const p = porId.get(id);
      return p ? nombre(p) : undefined;
    };
    const filas = filasDichas(utilidadPorProducto(datos.ventas, nombreDe, actual), t);
    const parados = mercaderiaParada({
      productos: datos.productos,
      existencias: datos.existencias,
      ultimaVenta: ultimaVentaPorProducto(datos.ventas),
      ultimaCompra: ultimaCompraPorProducto(datos.compras),
      hoy: hoyEs,
      dias: DIAS_PARADO,
    });
    const estante = { productos: datos.productos, existencias: datos.existencias, ventas: datos.ventas, reempaques: datos.reempaques, hoy: hoyEs, primeraVenta: datos.primeraVenta };
    return {
      nombreDe,
      porId,
      mejores: delCatalogo(filas).filter((x) => x.utilidad > 0),
      conc: concentracion(filas),
      bajos: bajoElMargen(filas, datos.minimoBp),
      parados,
      parado: parados.reduce((a, p) => a + p.valor, 0),
      agotados: agotados({ ...estante, salidas: datos.salidas, conteos: datos.conteos }),
      sobrantes: muchoEnElEstante(estante),
    };
  }, [datos, actual, hoyEs, nombre]);

  if (!datos || !calculo) return <Cargando />;
  const { nombreDe, porId, mejores, conc, bajos, parados, parado, agotados: sinNada, sobrantes } = calculo;
  const ultimaVez = (fecha: string | null): string =>
    fecha
      ? t('analisis.ultimaVenta', { fecha: f.corta(fecha) })
      : datos.primeraVenta !== null && datos.primeraVenta < datos.ventana
        ? t('analisis.masDe90')
        : t('analisis.nuncaVendido');

  return (
    <div className="flex flex-col gap-4" data-analisis="productos">
      <Seccion titulo={t('analisis.masDeja')} ayuda={t('analisis.masDejaAyuda')} datos="mas-deja">
        {mejores.length === 0 ? (
          <Nada texto={t('analisis.sinVentas')} />
        ) : (
          <Barras
            filas={mejores.slice(0, CUANTOS).map((x) => ({
              clave: x.clave,
              etiqueta: <AProducto id={x.clave} nombre={nombreDe(x.clave) ?? x.nombre} />,
              valor: x.utilidad,
              texto: dinero(x.utilidad),
              detalle: t('analisis.vendidoMargen', { monto: dinero(x.ingreso), pct: pct(x.margenBp) }),
            }))}
          />
        )}
        {conc && (
          <p className="mt-4 rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-300" data-concentracion>
            {t('analisis.concentracion', { n: conc.productos, total: conc.deTantos })}
          </p>
        )}
      </Seccion>

      <PorCategoria ventas={datos.ventas} productos={datos.productos} existencias={datos.existencias} tramo={actual} dinero={dinero} />

      <Seccion titulo={t('analisis.bajoMargen', { pct: pct(datos.minimoBp) })} ayuda={t('analisis.bajoMargenAyuda')} datos="bajo-margen">
        {bajos.length === 0 ? (
          <Nada texto={t('analisis.todosSobreMargen')} />
        ) : (
          <Corta filas={bajos}>
            {(vistas) => (
              <ul className="-my-2.5 divide-y divide-slate-800">
                {vistas.map((x) => (
                  <Fila
                    key={x.clave}
                    nombre={x.clave}
                    a={<AProducto id={x.clave} nombre={nombreDe(x.clave) ?? x.nombre} />}
                    detalle={t('analisis.vendidoVeces', { monto: dinero(x.ingreso), n: x.ventas })}
                    valor={(x.margenBp ?? 0) < 0 ? `${pct(x.margenBp)} · ${t('analisis.aPerdida')}` : pct(x.margenBp)}
                    rojo={(x.margenBp ?? 0) < 0}
                  />
                ))}
              </ul>
            )}
          </Corta>
        )}
      </Seccion>

      <Agotados lista={sinNada} productos={porId} dinero={dinero} />

      <MuchoEnElEstante lista={sobrantes} productos={porId} dinero={dinero} />

      <Seccion titulo={t('analisis.parada')} ayuda={t('analisis.paradaAyuda')} datos="parada">
        {parados.length === 0 ? (
          <Nada texto={t('analisis.nadaParado')} />
        ) : (
          <>
            <p className="mb-2 text-sm text-slate-300" data-parado-total>
              {t('analisis.paradaTotal', { monto: dinero(parado), n: parados.length })}
            </p>
            <Corta filas={parados}>
              {(vistas) => (
                <ul className="divide-y divide-slate-800">
                  {vistas.map((p) => {
                    const producto = porId.get(p.productoId);
                    return (
                      <Fila
                        key={p.productoId}
                        nombre={p.productoId}
                        a={<AProducto id={p.productoId} nombre={producto ? nombre(producto) : p.nombre} />}
                        detalle={`${formatQuantity(p.cantidad)} ${etiquetaUnidad(p.unidad, p.unidad)} · ${ultimaVez(p.ultimaVenta)}`}
                        valor={dinero(p.valor)}
                      />
                    );
                  })}
                </ul>
              )}
            </Corta>
          </>
        )}
      </Seccion>

      <SimularUnPrecio ventas={datos.ventas} hoyEs={hoyEs} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clientes
// ---------------------------------------------------------------------------

function VistaClientes({ tramos, hoyEs, dinero }: { tramos: Tramos; hoyEs: string; dinero: DineroDeAnalisis }) {
  const t = useT();
  const f = useFechas();
  const { actual } = tramos;
  const datos = useLiveQuery(async () => {
    const clientes = await db.clientes.toArray();
    const deudores = clientes.filter((c) => (c.saldo ?? 0) > 0);
    const ids = deudores.map((c) => c.id);
    // The regulars who stopped are measured over the four months before today, and each one's
    // rhythm over half a year.
    const desde = masAntigua(actual.desde, sumarDias(hoyEs, -(DIAS_DE_CLIENTE - 1)));
    const [fiados, abonos, devoluciones, incobrables, ventas, abonosDelPeriodo, perdidosDelPeriodo] = await Promise.all([
      db.ventas.where('clienteId').anyOf(ids).toArray(),
      db.abonos.where('clienteId').anyOf(ids).toArray(),
      db.devoluciones.where('clienteId').anyOf(ids).toArray(),
      db.incobrables.where('clienteId').anyOf(ids).toArray(),
      ventasDelPeriodo({ desde, hasta: hoyEs }),
      db.abonos.where('fecha').between(actual.desde, actual.hasta, true, true).toArray(),
      db.incobrables.where('fecha').between(actual.desde, actual.hasta, true, true).toArray(),
    ]);
    return {
      nombres: new Map(clientes.map((c) => [c.id, c.nombre])),
      cuentas: cuentasConFiado({ deudores, ventas: fiados, abonos, devoluciones, incobrables }),
      riesgos: riesgoDelFiado({ deudores, ventas: fiados, abonos, devoluciones, incobrables, hoy: hoyEs }),
      abonosDelPeriodo,
      perdidosDelPeriodo,
      ventas,
    };
  }, [actual.desde, actual.hasta, hoyEs]);

  const calculo = useMemo(() => {
    if (!datos) return null;
    return {
      fiado: fiadoPorAntiguedad(datos.cuentas, hoyEs),
      mejores: utilidadPorCliente(datos.ventas, (id) => datos.nombres.get(id) ?? t('utilidad.clienteEliminado'), actual)
        .filter((x) => x.clave !== 'mostrador')
        .sort((a, b) => b.ingreso - a.ingreso),
      idos: clientesQueSeFueron(datos.ventas, hoyEs),
      toca: lesTocaComprar(datos.ventas, hoyEs),
      fiadoVa: fiadoDelPeriodo({ ventas: datos.ventas, abonos: datos.abonosDelPeriodo, incobrables: datos.perdidosDelPeriodo, tramo: actual }),
    };
  }, [datos, actual, hoyEs]);

  if (!datos || !calculo) return <Cargando />;
  const { fiado, mejores, idos, toca, fiadoVa } = calculo;
  const nombreDe = (id: string) => datos.nombres.get(id) ?? '—';

  return (
    <div className="flex flex-col gap-4" data-analisis="clientes">
      <Seccion titulo={t('analisis.fiado')} ayuda={t('analisis.fiadoAyuda')} datos="fiado">
        {fiado.total <= 0 ? (
          <Nada texto={t('analisis.sinFiado')} />
        ) : (
          <>
            <p className="mb-3 text-sm text-slate-300" data-fiado-total>
              {t('analisis.fiadoTotal', { monto: dinero(fiado.total), n: fiado.clientes.length })}
            </p>
            <Barras
              filas={fiado.tramos.map((x) => ({
                clave: x.tramo,
                etiqueta: t(TRAMO[x.tramo].nombre),
                valor: x.monto,
                texto: dinero(x.monto),
                detalle: t('analisis.nClientes', { n: x.clientes }),
                tono: TRAMO[x.tramo].tono,
              }))}
              datos="tramos"
            />
            <h3 className="mt-5 text-sm font-semibold text-slate-300">{t('analisis.debenHace')}</h3>
            <Corta filas={fiado.clientes}>
              {(vistas) => (
                <ul className="divide-y divide-slate-800">
                  {vistas.map((c) => (
                    <Fila
                      key={c.clienteId}
                      nombre={c.clienteId}
                      a={<ACliente id={c.clienteId} nombre={c.nombre} />}
                      detalle={t('analisis.haceDias', { n: c.dias })}
                      valor={dinero(c.saldo)}
                      rojo={c.dias > 90}
                    />
                  ))}
                </ul>
              )}
            </Corta>
          </>
        )}
      </Seccion>

      <ComoVaElFiado periodo={fiadoVa} riesgos={datos.riesgos} dinero={dinero} />

      <Seccion titulo={t('analisis.mejoresClientes')} ayuda={t('analisis.mejoresAyuda')} datos="mejores">
        {mejores.length === 0 ? (
          <Nada texto={t('analisis.sinClientes')} />
        ) : (
          <Barras
            filas={mejores.slice(0, CUANTOS).map((x) => ({
              clave: x.clave,
              etiqueta: <ACliente id={x.clave} nombre={x.nombre} />,
              valor: x.ingreso,
              texto: dinero(x.ingreso),
              detalle: t('analisis.comprasUtilidad', { n: x.ventas, monto: dinero(x.utilidad) }),
            }))}
            tono="bg-violet-500"
          />
        )}
      </Seccion>

      <LesTocaComprar lista={toca} nombres={datos.nombres} dinero={dinero} />

      <Seccion titulo={t('analisis.seFueron')} ayuda={t('analisis.seFueronAyuda')} datos="se-fueron">
        {idos.length === 0 ? (
          <Nada texto={t('analisis.nadieSeFue')} />
        ) : (
          <Corta filas={idos}>
            {(vistas) => (
              <ul className="-my-2.5 divide-y divide-slate-800">
                {vistas.map((c) => (
                  <Fila
                    key={c.clienteId}
                    nombre={c.clienteId}
                    a={<ACliente id={c.clienteId} nombre={nombreDe(c.clienteId)} />}
                    detalle={t('analisis.seFueDetalle', { fecha: f.corta(c.ultima), n: c.dias })}
                    valor={dinero(c.ingreso)}
                  />
                ))}
              </ul>
            )}
          </Corta>
        )}
      </Seccion>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gastos
// ---------------------------------------------------------------------------

/** The days the break-even is worked over: long enough that the month's rent is not one day's. */
const DIAS_EQUILIBRIO = 90;
/** «Lo que se pierde» month by month: half a year. */
const MESES_DE_PERDIDAS = 6;

function VistaGastos({ tramos, hoyEs, dinero, deUnMes }: { tramos: Tramos; hoyEs: string; dinero: DineroDeAnalisis; deUnMes: boolean }) {
  const t = useT();
  const f = useFechas();
  const locale = localeActual();
  const nombre = useNombreProducto();
  const { actual, anterior } = tramos;
  /** What a figure adds up, in a sheet: the expenses, one account's, or what was left. */
  const [abierto, setAbierto] = useState<{ que: 'gastos' } | { que: 'cuenta'; codigo: string; nombre: string } | { que: 'resultado' } | null>(null);
  const datos = useLiveQuery(async () => {
    const reciente = sumarDias(hoyEs, -(DIAS_EQUILIBRIO - 1));
    // What was lost, month by month, goes back six months.
    const seisMeses = `${otroMes(hoyEs.slice(0, 7), -(MESES_DE_PERDIDAS - 1))}-01`;
    const [libro, primero, compras, proveedores, productos, config, sesiones, ventas] = await Promise.all([
      diasDelRango({ desde: masAntigua(masAntigua(anterior.desde, reciente), seisMeses), hasta: hoyEs }),
      db.saldosDiarios.orderBy('fecha').first(),
      db.compras.toArray(),
      db.proveedores.toArray(),
      db.productos.toArray(),
      readSettings(),
      // A till opened the evening before the period counts for the hours it was open in it.
      db.sesiones.where('abiertaEn').aboveOrEqual(`${sumarDias(actual.desde, -1)}T00:00:00`).toArray(),
      ventasDelPeriodo(actual),
    ]);
    return {
      libro,
      primerDia: primero?.fecha ?? null,
      reciente,
      compras,
      proveedores,
      productos,
      config,
      seisMeses,
      horas: horasDeCaja({ sesiones, momentos: await momentosDeVentas(ventas, actual), tramo: actual, ahora: new Date().toISOString() }),
    };
  }, [anterior.desde, actual.desde, actual.hasta, hoyEs]);

  const calculo = useMemo(() => {
    if (!datos) return null;
    const ahora = estadoResultados(datos.libro, actual);
    const antes = estadoResultados(datos.libro, anterior);
    // Since the books began, if that is less than the days: a shop open a month has a month.
    const desde = datos.primerDia && datos.primerDia > datos.reciente ? datos.primerDia : datos.reciente;
    const dias = diasEntre(desde, hoyEs) + 1;
    const ultimos = estadoResultados(datos.libro, { desde, hasta: hoyEs });
    // Each of the last six months' losses, this one so far.
    const perdidasPorMes = Array.from({ length: MESES_DE_PERDIDAS }, (_, i) => {
      const mes = otroMes(hoyEs.slice(0, 7), i - (MESES_DE_PERDIDAS - 1));
      const fin = sumarDias(`${otroMes(mes, 1)}-01`, -1);
      return { mes, total: perdidasDelPeriodo(estadoResultados(datos.libro, { desde: `${mes}-01`, hasta: fin < hoyEs ? fin : hoyEs }).gastos).total };
    });
    return {
      ahora,
      antes,
      dias,
      ultimos,
      perdidasPorMes,
      eq: equilibrio({ gastos: ultimos.totalGastos, ingreso: ultimos.totalIngresos, utilidad: ultimos.utilidadBruta, dias }),
      alzas: alzasDeCosto(datos.compras, actual),
    };
  }, [datos, actual, anterior, hoyEs]);

  if (!datos || !calculo) return <Cargando />;
  const { ahora, antes, dias, ultimos, perdidasPorMes, eq, alzas } = calculo;
  const proveedor = (id: string) => datos.proveedores.find((p) => p.id === id)?.nombre ?? '—';
  const producto = (id: string) => datos.productos.find((p) => p.id === id);
  // Lines of under half a córdoba, cash rounding's mostly, read «C$ 0» here and are left out.
  const lineas = [...ahora.gastos].filter((l) => Math.abs(l.monto) >= 50).sort((a, b) => b.monto - a.monto);
  const deVentas = parte(ahora.totalGastos, ahora.totalIngresos);
  const cubre = eq.necesitaPorDia !== null ? eq.vendePorDia - eq.necesitaPorDia : null;

  return (
    <div className="flex flex-col gap-4" data-analisis="gastos">
      <div>
        <div className="grid grid-cols-2 gap-3">
          <Cifra
            nombre={t('analisis.gastosTotal')}
            valor={dinero(ahora.totalGastos)}
            debajo={<Cambio bp={cambioBp(ahora.totalGastos, antes.totalGastos)} alReves />}
            datos="gastos"
            onAbrir={() => setAbierto({ que: 'gastos' })}
          />
          <Cifra
            nombre={t('analisis.utilidadNeta')}
            valor={dinero(ahora.utilidadNeta)}
            negativo={ahora.utilidadNeta < 0}
            debajo={<Cambio bp={cambioBp(ahora.utilidadNeta, antes.utilidadNeta)} />}
            datos="neta"
            onAbrir={() => setAbierto({ que: 'resultado' })}
          />
        </div>
        {deVentas !== null && (
          <p className="mt-2 text-xs text-slate-500" data-de-ventas>
            {t('analisis.seLlevan', { pct: pct(deVentas) })}
          </p>
        )}
        {(antes.totalGastos > 0 || antes.utilidadNeta > 0) && (
          <p className="mt-1 text-xs text-slate-500" data-comparado>
            {t(esSoloHoy(actual, hoyEs) ? 'analisis.comparadoConDiaEntero' : 'analisis.comparadoCon', { rango: etiquetaRango(anterior.desde, anterior.hasta, hoyEs, locale) })}
          </p>
        )}
      </div>

      <Seccion titulo={t('analisis.enQueSeVa')} datos="lineas">
        {lineas.length === 0 ? (
          <Nada texto={t('analisis.sinGastos')} />
        ) : (
          <Barras
            filas={lineas.map((l) => {
              const deLoVendido = parte(l.monto, ahora.totalIngresos);
              return {
                clave: l.codigo,
                etiqueta: etiquetaCuenta(l.codigo, l.nombre),
                valor: l.monto,
                texto: dinero(l.monto),
                onAbrir: () => setAbierto({ que: 'cuenta', codigo: l.codigo, nombre: etiquetaCuenta(l.codigo, l.nombre) }),
                ...(deLoVendido !== null ? { detalle: t('analisis.parteDeVentas', { pct: pct(deLoVendido) }) } : {}),
              };
            })}
            tono="bg-emerald-500"
          />
        )}
      </Seccion>

      {deUnMes && <PresupuestoDelMes gastos={ahora.gastos} recientes={ultimos.gastos} config={datos.config} dinero={dinero} />}

      <LoQueSePierde gastos={ahora.gastos} antes={antes.gastos} ingresos={ahora.totalIngresos} meses={perdidasPorMes} config={datos.config} dinero={dinero} />

      <LaPlanilla ahora={ahora} antes={antes} dinero={dinero} />

      <Seccion titulo={t('analisis.equilibrio')} ayuda={t('analisis.equilibrioAyuda', { n: dias })} datos="equilibrio">
        {eq.necesitaPorDia === null || cubre === null ? (
          <Nada texto={t('analisis.sinEquilibrio')} />
        ) : (
          <div className="flex flex-col gap-2 text-sm">
            <p data-necesita>{t('analisis.equilibrioNecesita', { monto: dinero(eq.necesitaPorDia) })}</p>
            <p className="text-slate-400">{t('analisis.equilibrioVende', { monto: dinero(eq.vendePorDia) })}</p>
            <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-slate-800" aria-hidden>
              <div
                className={`h-full rounded-full ${cubre >= 0 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                style={{ width: `${Math.min(100, eq.necesitaPorDia > 0 ? (eq.vendePorDia / eq.necesitaPorDia) * 100 : 100)}%` }}
              />
            </div>
            <p className={`font-medium ${cubre >= 0 ? 'text-emerald-300' : 'text-amber-300'}`} data-cubre={cubre >= 0 ? 'si' : 'no'}>
              {cubre >= 0 ? t('analisis.sobra', { monto: dinero(cubre) }) : t('analisis.falta', { monto: dinero(-cubre) })}
            </p>
          </div>
        )}
      </Seccion>

      <MeAlcanza ultimos={{ ingreso: ultimos.totalIngresos, utilidadBruta: ultimos.utilidadBruta, utilidadNeta: ultimos.utilidadNeta }} dias={dias} config={datos.config} />

      <HorasDeCaja horas={datos.horas} />

      <Seccion titulo={t('analisis.alzas')} ayuda={t('analisis.alzasAyuda')} datos="alzas">
        {alzas.alzas.length === 0 ? (
          <Nada texto={t('analisis.sinAlzas')} />
        ) : (
          <>
            <ul className="mb-3 flex flex-col gap-1 text-sm text-slate-300" data-por-proveedor>
              {alzas.porProveedor.map((p) => (
                <li key={p.proveedorId}>
                  {t('analisis.porProveedor', { proveedor: proveedor(p.proveedorId), n: p.productos, pct: pct(p.promedioBp) })}
                </li>
              ))}
            </ul>
            <Corta filas={alzas.alzas}>
              {(vistas) => (
                <ul className="divide-y divide-slate-800">
                  {vistas.map((a) => {
                    const p = producto(a.productoId);
                    return (
                      <Fila
                        key={a.productoId}
                        nombre={a.productoId}
                        a={<AProducto id={a.productoId} nombre={p ? nombre(p) : '—'} />}
                        detalle={`${t('analisis.precioAntesAhora', {
                          antes: dinero.porUnidad(a.antes),
                          ahora: dinero.porUnidad(a.ahora),
                          unidad: p ? etiquetaUnidad(p.unidadBase, p.unidadBase) : '',
                        })} · ${proveedor(a.proveedorId)} · ${f.corta(a.fecha)}`}
                        valor={`+${pct(a.alzaBp)}`}
                        rojo
                      />
                    );
                  })}
                </ul>
              )}
            </Corta>
          </>
        )}
      </Seccion>
      {abierto?.que === 'gastos' && (
        <GastosDelPeriodo tramo={actual} titulo={t('analisis.detalle.gastos')} dinero={dinero} onCerrar={() => setAbierto(null)} />
      )}
      {abierto?.que === 'cuenta' && (
        <GastosDelPeriodo tramo={actual} cuenta={abierto.codigo} titulo={abierto.nombre} dinero={dinero} onCerrar={() => setAbierto(null)} />
      )}
      {abierto?.que === 'resultado' && <ResultadoDelPeriodo resultado={ahora} tramo={actual} dinero={dinero} onCerrar={() => setAbierto(null)} />}
    </div>
  );
}
